import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_SITE_BACKUP_API,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	isRecentQuery,
	LOOKUP_FALLBACK_LIMIT,
	LOOKUP_LIMIT,
	mostRecentRow,
	optionalText,
	rankLookupCandidates,
	responseRows,
} from '../../core/resolutionUtils';
import {resolveByFields} from '../../core/fieldSearch';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Site Backup row identifier.
 * @param {object} item Canvas site backup row
 * @return {*} identifier
 */
function siteBackupId(item) {
	return item?.backup_id ?? item?.id;
}

/**
 * Read the Site column as one comparable string.
 * @param {object} item Canvas site backup row
 * @return {string} joined site names
 */
function siteNameText(item) {
	return Array.isArray(item?.site_name)
		? item.site_name.filter(Boolean).join(', ')
		: optionalText(item?.site_name) || '';
}

/**
 * Copy a Canvas row with catalogued fields written as plain strings.
 *
 * The catalogue only ever names plain string fields for Site Backup - no
 * choice-coded values like alarm severity - so unlike alarms/reports there
 * is nothing to relabel here beyond normalizing the Site column.
 *
 * @param {object} item Canvas site backup row
 * @return {object} row the generic lookup can read
 */
function rowForSearch(item) {
	return {
		...item,
		site_name: siteNameText(item),
	};
}

/**
 * Normalize a Canvas Site Backup row.
 * @param {object} item Canvas site backup row
 * @return {object|null} agent candidate
 */
function normalizeSiteBackup(item) {
	const id = siteBackupId(item);
	const name = item?.backup_name;
	if (id == null || !name) return null;
	return {
		entity_type: 'site_backup',
		entity_id: String(id),
		name: String(name),
		site_id: null,
		site_name: siteNameText(item) || null,
		serial_no: null,
	};
}

/**
 * Load Site Backup rows, paging until the lookup cap.
 * @param {string|null} search name search
 * @param {number} limit maximum rows
 * @return {Promise<Array>} rows
 */
async function loadRows(search, limit) {
	const rows = [];
	const pageSize = Math.min(Math.max(limit, 1), LOOKUP_LIMIT);
	let offset = 0;
	while (rows.length < limit) {
		const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
		const url = `${ENDPOINT_URL}${GET_SITE_BACKUP_API}` +
			`?limit=${pageSize}&offset=${offset}${searchParam}`;
		const {data} = await axios.get(url);
		const page = responseRows(decodeAgentResponse(data) || {});
		rows.push(...page);
		if (page.length < pageSize) break;
		offset += pageSize;
	}
	return rows.slice(0, limit);
}

/**
 * Rank loaded rows through the catalogue the agent sent.
 * @param {Array} rows Canvas rows
 * @param {object} params resolver parameters
 * @param {string} query lookup query
 * @param {number} limit maximum candidates
 * @param {boolean} [fuzzy] allow near-miss matches
 * @return {Array} ranked candidates
 */
function candidatesFromRows(rows, params, query, limit, fuzzy = false) {
	return resolveByFields({
		rows: rows.map(rowForSearch),
		normalizeRow: normalizeSiteBackup,
		query,
		limit,
		searchFields: params.search_fields,
		rank: rankLookupCandidates,
		fallbackValues: (candidate) => [candidate?.name, candidate?.site_name],
		fuzzy,
	});
}

/**
 * Resolve Site Backup candidates, paging past the first table page.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSiteBackups(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (isRecentQuery(query)) {
		// "Recent" is newest Created At, not Refresh, and not the same as a
		// plain lookup: rank by text match would put an exact-named row
		// ahead of a newer one, which is the wrong answer to "the recent
		// backup". Loaded at the fallback page size so the newest row is
		// found even when it fell off the first table page.
		const rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		replaceResolvedEntities('site_backup', rows, siteBackupId);
		const newest = mostRecentRow(rows, 'created_at');
		return {candidates: newest ? [normalizeSiteBackup(newest)] : []};
	}
	if (!query && !browse) throw new Error('lookup_query_required');
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('site_backup', rows, siteBackupId);
		return {
			candidates: candidatesFromRows(rows, params, '', limit)
				.slice(0, limit),
		};
	}
	let rows = await loadRows(query, LOOKUP_LIMIT);
	let candidates = candidatesFromRows(rows, params, query, LOOKUP_LIMIT);
	// The list API already applied `search`. If the catalogue then drops
	// every row, keep the API hits instead of pretending nothing matched.
	if (!candidates.length && rows.length) {
		candidates = candidatesFromRows(rows, params, '', LOOKUP_LIMIT);
	}
	if (!candidates.length) {
		rows = await loadRows(null, LOOKUP_FALLBACK_LIMIT);
		candidates = candidatesFromRows(rows, params, query, LOOKUP_LIMIT);
		if (!candidates.length && params.fuzzy === true) {
			candidates = candidatesFromRows(
				rows, params, query, LOOKUP_LIMIT, true,
			);
		}
	}
	replaceResolvedEntities('site_backup', rows, siteBackupId);
	return {candidates};
}

/**
 * Return a retained Site Backup row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedSiteBackup(entityId) {
	return getResolvedEntity('site_backup', entityId);
}
